import { useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { deploymentProfile } from '../../config/deploymentProfile';

/**
 * Compute a contrasting text color for the chart's background.
 * Handles hex (#RRGGBB) and rgb/rgba() strings.
 */
function getContrastColor(background) {
  const c = background || '#ffffff';
  let r = 255, g = 255, b = 255;
  if (c.startsWith('#') && c.length >= 7) {
    r = parseInt(c.slice(1, 3), 16);
    g = parseInt(c.slice(3, 5), 16);
    b = parseInt(c.slice(5, 7), 16);
  } else if (c.startsWith('rgb')) {
    const m = c.match(/[\d.]+/g);
    if (m && m.length >= 3) { r = +m[0]; g = +m[1]; b = +m[2]; }
  }
  return (0.299 * r + 0.587 * g + 0.114 * b) < 128 ? '#F5F5F5' : '#111111';
}

/**
 * Shared tooltip hook for all chart preview components.
 *
 * Usage:
 *   const { containerRef, showTip, hideTooltip, Tip } = useChartTooltip(theme);
 *
 * In event handlers pass a pre-formatted string:
 *   onMouseMove={(e) => showTip(e, value.toFixed(1))}
 *
 * Render anywhere inside the component:
 *   <Tip />
 *
 * Uses position:fixed so the tooltip is always visible at the cursor
 * and can never overflow the chart container or trigger scrollbars.
 */
export function useChartTooltip(theme) {
  const containerRef = useRef(null);
  const [tip, setTip] = useState(null); // { x, y, text } in viewport coords

  const tooltipFg = getContrastColor(theme?.background);

  const showTip = useCallback((event, text) => {
    setTip({
      x: event.clientX,
      y: event.clientY,
      text,
    });
  }, []);

  const hideTooltip = useCallback(() => setTip(null), []);

  const tooltipBg = (theme?.background || '#ffffff') + 'CC';

  const Tip = () =>
    tip
      ? createPortal(
          <div
            style={{
              position: 'fixed',
              left: tip.x + 12,
              top: tip.y - 16,
              zIndex: 9999,
              color: tooltipFg,
              fontSize: 13,
              fontWeight: 600,
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
              fontFamily: theme?.fontNumber || theme?.fontBody || deploymentProfile.font.numberFallbackStack || deploymentProfile.font.reactFallbackStack,
              backgroundColor: tooltipBg,
              padding: '2px 6px',
              borderRadius: 4,
            }}
          >
            {tip.text}
          </div>,
          document.body,
        )
      : null;

  return { containerRef, showTip, hideTooltip, Tip };
}
